import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { SiTranslateService, SiTranslateServiceBuilder, SiTranslateModule } from '@simpl/element-ng/translate';
import * as i1 from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * {@link SiTranslateService} wrapper around ngx-translate
 *
 * @internal
 */
class SiTranslateNgxTService extends SiTranslateService {
    ngxTranslateService;
    defaultTranslations = {};
    originalMissingHandler;
    constructor(ngxTranslateService) {
        super();
        this.ngxTranslateService = ngxTranslateService;
        this.originalMissingHandler = ngxTranslateService.missingTranslationHandler;
        ngxTranslateService.missingTranslationHandler = {
            handle: params => this.handleMissingTranslation(params)
        };
        this.translationChange$ = merge(this.ngxTranslateService.onTranslationChange, this.ngxTranslateService.onLangChange).pipe(map(() => { }));
    }
    get currentLanguage() {
        return this.ngxTranslateService.currentLang;
    }
    get availableLanguages() {
        return this.ngxTranslateService.getLangs();
    }
    set availableLanguages(languages) {
        this.ngxTranslateService.addLangs(languages);
    }
    setCurrentLanguage(lang) {
        this.setDocumentLanguage(lang);
        return this.ngxTranslateService.use(lang);
    }
    getDefaultLanguage() {
        return this.ngxTranslateService.getDefaultLang();
    }
    setDefaultLanguage(lang) {
        this.setDocumentLanguage(lang);
        this.ngxTranslateService.setDefaultLang(lang);
    }
    translate(keys, params) {
        return this.ngxTranslateService.stream(keys, params);
    }
    translateAsync(keys, params) {
        return this.ngxTranslateService.stream(keys, params);
    }
    translateSync(keys, params) {
        return this.ngxTranslateService.instant(keys, params);
    }
    setTranslation(key, value) {
        this.defaultTranslations[key] = value;
    }
    handleMissingTranslation(params) {
        return (params.translateService.parser.interpolate(this.defaultTranslations[params.key], params.interpolateParams) ?? this.originalMissingHandler.handle(params));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTService, deps: [{ token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.TranslateService }]; } });

/**
 * Builds {@link SiTranslateNgxTService} for each {@link TranslateService}.
 * The builder will create a new Service if a new {@link TranslateService} is found to support the isolated mode.
 *
 * @internal
 */
class SiTranslateNgxTServiceBuilder extends SiTranslateServiceBuilder {
    serviceCache = new Map();
    buildService(injector) {
        // Get instance of NGX Translate via injector instance of the current scope (see isolated mode)
        const ngxTranslateService = injector.get(TranslateService);
        let ngxTServiceWrapper = this.serviceCache.get(ngxTranslateService);
        if (!ngxTServiceWrapper) {
            ngxTServiceWrapper = new SiTranslateNgxTService(ngxTranslateService);
            this.serviceCache.set(ngxTranslateService, ngxTServiceWrapper);
        }
        return ngxTServiceWrapper;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTServiceBuilder, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTServiceBuilder });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTServiceBuilder, decorators: [{
            type: Injectable
        }] });

/**
 * This module configures Element to use ngx-translate for translating {@link TranslatableString}
 * It should only be imported once in an applications root module (typically `app.module.ts`)
 */
class SiTranslateNgxTModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTModule, imports: [SiTranslateModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTModule, providers: [{ provide: SiTranslateServiceBuilder, useClass: SiTranslateNgxTServiceBuilder }], imports: [SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateNgxTModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [{ provide: SiTranslateServiceBuilder, useClass: SiTranslateNgxTServiceBuilder }],
                    imports: [SiTranslateModule]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiTranslateNgxTModule };
